import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  RefreshButton,
  Select,
  Table,
  Text,
  VStack,
} from "components/ui";
import { toaster } from "components/ui/toaster";
import { useSuggestions } from "contexts/SuggestionsContext";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";
import {
  MdAdd,
  MdCheck,
  MdClose,
  MdDelete,
  MdEdit,
  MdExpandMore,
  MdRefresh,
  MdShield,
} from "react-icons/md";
import {
  approveSuggestedRules,
  declineSuggestedRules,
  deleteMaskingRule,
  getMaskingRules,
  getSuggestedMaskingRules,
  MaskingRuleDto,
  MaskingRuleScope,
} from "services/maskingService";
import { getThemeColor } from "theme/colors";
import CreateMaskingRuleModal from "./components/CreateMaskingRuleModal";
import EditMaskingRuleModal from "./components/EditMaskingRuleModal";
import TestMaskingModal from "./components/TestMaskingModal";

interface MaskingRule {
  id: string;
  name: string;
  description?: string;
  fieldPath: string;
  keepFirstChars: number;
  keepLastChars: number;
  maskingSymbol: string;
  isActive: boolean;
  priority: number;
  scope: MaskingRuleScope;
  serverId?: string;
  serverName?: string;
  toolName?: string;
  createdAt: string;
  updatedAt: string;
}

const getFieldSegments = (fieldPath: string): string[] =>
  (fieldPath.split(".").pop() ?? fieldPath)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .map((s) => s.toLowerCase())
    .filter(Boolean);

const getMockValueForField = (fieldPath: string): string => {
  const segs = getFieldSegments(fieldPath);
  const has = (...words: string[]) => segs.some((s) => words.includes(s));
  if (has("email", "mail")) return "roman.v@gmail.com";
  if (has("phone", "mobile")) return "+12025559708";
  if (has("card", "pan")) return "4111111111111234";
  if (has("password", "pass", "passcode", "passphrase", "secret", "pin"))
    return "MyP@ssw0rd!";
  if (has("token", "bearer", "jwt")) return "eyJhbGciOiJSUzI1NiJ9abc";
  if ((has("api") && has("key")) || has("apikey", "apitoken"))
    return "sk-a7f3d9b2c1e4f8a0b5c2";
  if (has("ssn", "nationalid", "taxid")) return "123-45-6789";
  if (has("iban")) return "GB29NWBK60161331926819";
  if (has("cvv", "cvc")) return "742";
  if (has("passport")) return "AB1234567";
  if (has("dob", "birthdate", "dateofbirth") || (has("date") && has("birth")))
    return "1990-05-15";
  if (has("address", "street", "billing", "shipping"))
    return "123 Main Street, NY";
  if (has("name")) return "Roman Vevdiuk";
  if (has("account", "bank")) return "12345678901234";
  if (has("license", "driver")) return "D1234567";
  if (has("ip")) return "192.168.1.100";
  return "sensitive-data-value";
};

const getGroupForField = (fieldPath: string): string => {
  const segs = getFieldSegments(fieldPath);
  const has = (...words: string[]) => segs.some((s) => words.includes(s));
  if (
    has(
      "password",
      "pass",
      "passcode",
      "passphrase",
      "secret",
      "cvv",
      "cvc",
      "pin",
    )
  )
    return "Passwords & Secrets";
  if (
    has("token", "bearer", "jwt", "apikey", "apitoken") ||
    (has("api") && has("key"))
  )
    return "API Keys & Tokens";
  if (has("card", "pan", "account", "bank", "iban", "routing"))
    return "Payment & Financial";
  if (has("ssn", "nationalid", "taxid", "passport", "license", "driver"))
    return "Identity Documents";
  if (has("email", "mail", "phone", "mobile")) return "Contact Information";
  if (
    has(
      "name",
      "address",
      "street",
      "dob",
      "birthdate",
      "dateofbirth",
      "billing",
      "shipping",
    )
  )
    return "Personal Information";
  if (has("ip")) return "Network";
  return "Other";
};

const buildMaskPreview = (
  keepFirst: number,
  keepLast: number,
  symbol: string,
  value: string,
): string => {
  const first = value.slice(0, keepFirst);
  const last = keepLast > 0 ? value.slice(-keepLast) : "";
  const maskedLen = Math.max(value.length - keepFirst - keepLast, 3);
  return `${first}${(symbol || "*").repeat(maskedLen)}${last}`;
};

const MaskingRules = () => {
  const { t } = useTranslation();
  const { refreshCounts } = useSuggestions();
  const [rules, setRules] = useState<MaskingRule[]>([]);
  const [suggestions, setSuggestions] = useState<MaskingRuleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedRule, setSelectedRule] = useState<MaskingRule | null>(null);
  const [scopeFilter, setScopeFilter] = useState<string>("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const groupedSuggestions = useMemo(() => {
    const map = new Map<string, MaskingRuleDto[]>();
    suggestions.forEach((s) => {
      const group = getGroupForField(s.fieldPath);
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(s);
    });
    return Array.from(map.entries()).sort(
      (a, b) =>
        Math.max(...b[1].map((r) => r.priority)) -
        Math.max(...a[1].map((r) => r.priority)),
    );
  }, [suggestions]);

  // Expand all groups when suggestions first load
  useEffect(() => {
    if (suggestions.length > 0)
      setExpandedGroups(new Set(groupedSuggestions.map(([g]) => g)));
  }, [suggestions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (scopeFilter) params.scope = scopeFilter;
      const data = await getMaskingRules(params);
      setRules(data);
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const data = await getSuggestedMaskingRules();
      setSuggestions(data);
    } catch (err) {
      console.error("[MaskingRules] Failed to load suggested rules:", err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    loadRules();
    loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeFilter]);

  const handleActivateOne = async (id: string) => {
    setApprovingIds((prev) => new Set(prev).add(id));
    try {
      await approveSuggestedRules([id]);
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      loadRules();
      refreshCounts();
      toaster.create({
        type: "success",
        title: t("app.security.masking.suggestions.activated", { count: 1 }),
        duration: 3000,
      });
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.approveFailed"));
    } finally {
      setApprovingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  };

  const handleDismissOne = async (id: string) => {
    setDismissingIds((prev) => new Set(prev).add(id));
    try {
      await declineSuggestedRules([id]);
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      refreshCounts();
      toaster.create({
        type: "info",
        title: t("app.security.masking.suggestions.dismissed"),
        duration: 2000,
      });
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.declineFailed"));
    } finally {
      setDismissingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  };

  const handleActivateGroup = async (ids: string[]) => {
    try {
      await approveSuggestedRules(ids);
      setSuggestions((prev) => prev.filter((s) => !ids.includes(s.id)));
      loadRules();
      refreshCounts();
      toaster.create({
        type: "success",
        title: t("app.security.masking.suggestions.activated", {
          count: ids.length,
        }),
        duration: 3000,
      });
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.approveFailed"));
    }
  };

  const handleDismissGroup = async (ids: string[]) => {
    try {
      await declineSuggestedRules(ids);
      setSuggestions((prev) => prev.filter((s) => !ids.includes(s.id)));
      refreshCounts();
      toaster.create({
        type: "info",
        title: t("app.security.masking.suggestions.dismissed"),
        duration: 2000,
      });
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.declineFailed"));
    }
  };

  const handleActivateAll = async () => {
    const ids = suggestions.map((s) => s.id);
    try {
      await approveSuggestedRules(ids);
      setSuggestions([]);
      loadRules();
      refreshCounts();
      toaster.create({
        type: "success",
        title: t("app.security.masking.suggestions.activated", {
          count: ids.length,
        }),
        duration: 3000,
      });
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.approveFailed"));
    }
  };

  const handleDismissAll = async () => {
    const ids = suggestions.map((s) => s.id);
    try {
      await declineSuggestedRules(ids);
      setSuggestions([]);
      refreshCounts();
      toaster.create({
        type: "info",
        title: t("app.security.masking.suggestions.dismissed"),
        duration: 2000,
      });
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.declineFailed"));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("app.security.masking.deleteConfirm"))) return;
    try {
      await deleteMaskingRule(id);
      setSuccess(t("app.security.masking.deleteSuccess"));
      setTimeout(() => setSuccess(""), 3000);
      loadRules();
    } catch (err: any) {
      setError(err.message || t("app.security.masking.errors.deleteFailed"));
    }
  };

  const handleEdit = (rule: MaskingRule) => {
    setSelectedRule(rule);
    setEditModalOpen(true);
  };

  const getScopeBadgeColor = (scope: MaskingRuleScope) => {
    switch (scope) {
      case MaskingRuleScope.Global:
        return "blue";
      case MaskingRuleScope.Server:
        return "purple";
      case MaskingRuleScope.ServerTool:
        return "orange";
      default:
        return "gray";
    }
  };

  const getScopeLabel = (scope: MaskingRuleScope) => {
    switch (scope) {
      case MaskingRuleScope.Global:
        return t("app.security.masking.scope.global");
      case MaskingRuleScope.Server:
        return t("app.security.masking.scope.server");
      case MaskingRuleScope.ServerTool:
        return t("app.security.masking.scope.serverTool");
      default:
        return t("app.security.masking.scope.unknown");
    }
  };

  return (
    <Box>
      <VStack align="stretch" gap={4}>
        {/* Pending AI Suggestions */}
        {!loadingSuggestions && suggestions.length > 0 && (
          <Card>
            <VStack align="stretch" gap={3}>
              <HStack justify="space-between">
                <HStack gap={2}>
                  <MdShield size={20} color="orange" />
                  <Heading size="md">
                    {t("app.security.masking.suggestions.title", {
                      count: suggestions.length,
                    })}
                  </Heading>
                </HStack>
                <HStack gap={2}>
                  <Button
                    size="sm"
                    colorPalette="green"
                    onClick={handleActivateAll}
                  >
                    <MdCheck style={{ marginRight: "0.4rem" }} />
                    {t("app.security.masking.suggestions.activateAll")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDismissAll}
                  >
                    {t("app.security.masking.suggestions.dismissAll")}
                  </Button>
                </HStack>
              </HStack>

              <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                {t("app.security.masking.suggestions.subtitle")}
              </Text>

              <VStack align="stretch" gap={2}>
                {groupedSuggestions.map(([group, items]) => {
                  const isExpanded = expandedGroups.has(group);
                  const ids = items.map((s) => s.id);
                  return (
                    <Box
                      key={group}
                      border="1px solid"
                      borderColor={getThemeColor("border.default")}
                      borderRadius="md"
                      overflow="hidden"
                    >
                      {/* Group header */}
                      <Flex
                        px={4}
                        py={2.5}
                        bg={getThemeColor("bg.secondary")}
                        align="center"
                        justify="space-between"
                        cursor="pointer"
                        onClick={() => toggleGroup(group)}
                      >
                        <HStack gap={2}>
                          <MdExpandMore
                            size={18}
                            style={{
                              transform: isExpanded
                                ? "rotate(0deg)"
                                : "rotate(-90deg)",
                              transition: "transform 0.2s",
                              flexShrink: 0,
                            }}
                          />
                          <Text fontWeight="semibold" fontSize="sm">
                            {group}
                          </Text>
                          <Badge colorPalette="orange" variant="subtle">
                            {items.length}
                          </Badge>
                        </HStack>
                        <HStack gap={2}>
                          <Button
                            size="xs"
                            colorPalette="green"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActivateGroup(ids);
                            }}
                          >
                            {t("app.security.masking.suggestions.activateAll")}
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDismissGroup(ids);
                            }}
                          >
                            {t("app.security.masking.suggestions.dismissAll")}
                          </Button>
                        </HStack>
                      </Flex>

                      {/* Expanded rows */}
                      {isExpanded && (
                        <Box overflowX="auto">
                          <Table.Root minW="700px">
                            <Table.Header>
                              <Table.Row>
                                <Table.ColumnHeader>
                                  {t(
                                    "app.security.masking.suggestions.fieldPath",
                                  )}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader>
                                  {t("app.security.masking.table.name")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader>
                                  {t("app.security.masking.table.serverTool")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader>
                                  {t(
                                    "app.security.masking.suggestions.preview",
                                  )}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader>
                                  {t("app.common.actions")}
                                </Table.ColumnHeader>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {items.map((s) => (
                                <Table.Row key={s.id}>
                                  <Table.Cell>
                                    <code>{s.fieldPath}</code>
                                  </Table.Cell>
                                  <Table.Cell>
                                    <VStack align="start" gap={0}>
                                      <Text fontWeight="medium" fontSize="sm">
                                        {s.name}
                                      </Text>
                                      {s.description && (
                                        <Text
                                          fontSize="xs"
                                          color={getThemeColor(
                                            "text.secondary",
                                          )}
                                        >
                                          {s.description}
                                        </Text>
                                      )}
                                    </VStack>
                                  </Table.Cell>
                                  <Table.Cell>
                                    {s.serverName && (
                                      <Text
                                        fontSize="sm"
                                        color={getThemeColor("text.secondary")}
                                      >
                                        {s.serverName}
                                      </Text>
                                    )}
                                  </Table.Cell>
                                  <Table.Cell>
                                    <code style={{ fontSize: "0.8rem" }}>
                                      {buildMaskPreview(
                                        s.keepFirstChars,
                                        s.keepLastChars,
                                        s.maskingSymbol,
                                        getMockValueForField(s.fieldPath),
                                      )}
                                    </code>
                                  </Table.Cell>
                                  <Table.Cell>
                                    <HStack gap={2}>
                                      <Button
                                        size="sm"
                                        colorPalette="green"
                                        variant="outline"
                                        loading={approvingIds.has(s.id)}
                                        onClick={() => handleActivateOne(s.id)}
                                      >
                                        <MdCheck
                                          style={{ marginRight: "0.3rem" }}
                                        />
                                        {t(
                                          "app.security.masking.suggestions.activateOne",
                                        )}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        colorPalette="gray"
                                        loading={dismissingIds.has(s.id)}
                                        onClick={() => handleDismissOne(s.id)}
                                      >
                                        <MdClose />
                                      </Button>
                                    </HStack>
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                            </Table.Body>
                          </Table.Root>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </VStack>
          </Card>
        )}

        <Card>
          <VStack align="stretch" gap={4}>
            {error && (
              <InfoBox variant="error">
                <HStack justify="space-between">
                  <Text color="red.700">{error}</Text>
                  <Button
                    onClick={() => setError("")}
                    size="sm"
                    variant="ghost"
                  >
                    {t("app.common.dismiss")}
                  </Button>
                </HStack>
              </InfoBox>
            )}

            {success && (
              <InfoBox variant="success">
                <HStack justify="space-between">
                  <Text color="green.700">{success}</Text>
                  <Button
                    onClick={() => setSuccess("")}
                    size="sm"
                    variant="ghost"
                  >
                    {t("app.common.dismiss")}
                  </Button>
                </HStack>
              </InfoBox>
            )}

            <HStack justify="space-between" wrap="wrap" gap={3}>
              <Heading size="lg">{t("app.security.masking.title")}</Heading>
              <HStack gap={3}>
                <AskStrikeUpButton
                  context={t("app.askStrikeUp.context.masking")}
                />
                <RefreshButton onClick={loadRules} loading={loading} />
                <Button
                  intent="secondary"
                  onClick={() => setTestModalOpen(true)}
                >
                  {t("app.security.masking.testMasking")}
                </Button>
                <Button
                  intent="primary"
                  onClick={() => setCreateModalOpen(true)}
                >
                  <MdAdd />
                  {t("app.security.masking.newRule")}
                </Button>
              </HStack>
            </HStack>

            <HStack>
              <Text fontSize="sm" fontWeight="medium">
                {t("app.security.masking.filterByScope")}
              </Text>
              <Select
                size="sm"
                width="200px"
                value={[scopeFilter]}
                onValueChange={(details: any) =>
                  setScopeFilter(details.value[0] || "")
                }
                items={[
                  { label: t("app.filters.all"), value: "" },
                  {
                    label: t("app.security.masking.scope.global"),
                    value: "Global",
                  },
                  {
                    label: t("app.security.masking.scope.server"),
                    value: "Server",
                  },
                  {
                    label: t("app.security.masking.scope.serverTool"),
                    value: "ServerTool",
                  },
                ]}
              />
            </HStack>

            {loading ? (
              <Text>{t("app.common.loading")}</Text>
            ) : rules.length === 0 ? (
              <Box textAlign="center" py={8}>
                <Text color={getThemeColor("text.secondary")}>
                  {t("app.security.masking.noRulesFound")}
                </Text>
                <Button
                  mt={4}
                  intent="primary"
                  onClick={() => setCreateModalOpen(true)}
                >
                  <MdAdd />
                  {t("app.security.masking.createFirstRule")}
                </Button>
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table.Root minW="900px">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>
                        {t("app.security.masking.table.name")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.security.masking.table.fieldPath")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.security.masking.table.scope")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.security.masking.table.serverTool")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.security.masking.table.keepChars")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.security.masking.table.symbol")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.security.masking.table.priority")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.common.status")}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>
                        {t("app.common.actions")}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rules.map((rule) => (
                      <Table.Row key={rule.id}>
                        <Table.Cell>
                          <VStack align="start" gap={0}>
                            <Text fontWeight="medium">{rule.name}</Text>
                            {rule.description && (
                              <Text
                                fontSize="xs"
                                color={getThemeColor("text.secondary")}
                              >
                                {rule.description}
                              </Text>
                            )}
                          </VStack>
                        </Table.Cell>
                        <Table.Cell>
                          <code>{rule.fieldPath}</code>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge colorPalette={getScopeBadgeColor(rule.scope)}>
                            {getScopeLabel(rule.scope)}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          {rule.scope !== MaskingRuleScope.Global && (
                            <VStack align="start" gap={0}>
                              {rule.serverName && (
                                <Text fontSize="sm">{rule.serverName}</Text>
                              )}
                              {rule.toolName && (
                                <Text
                                  fontSize="xs"
                                  color={getThemeColor("text.secondary")}
                                >
                                  {rule.toolName}
                                </Text>
                              )}
                            </VStack>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">
                            {rule.keepFirstChars} / {rule.keepLastChars}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <code>{rule.maskingSymbol}</code>
                        </Table.Cell>
                        <Table.Cell>{rule.priority}</Table.Cell>
                        <Table.Cell>
                          <Badge colorPalette={rule.isActive ? "green" : "red"}>
                            {rule.isActive
                              ? t("app.common.active")
                              : t("app.common.inactive")}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap={2}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(rule)}
                            >
                              <MdEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() => handleDelete(rule.id)}
                            >
                              <MdDelete />
                            </Button>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </VStack>
        </Card>
      </VStack>

      <CreateMaskingRuleModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={loadRules}
      />

      {selectedRule && (
        <EditMaskingRuleModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          rule={selectedRule}
          onSuccess={loadRules}
        />
      )}

      <TestMaskingModal
        isOpen={testModalOpen}
        onClose={() => setTestModalOpen(false)}
      />
    </Box>
  );
};

export default MaskingRules;
